import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { modelApi, type RelatedOperationRow } from '../../api/modelApi';
import './RelatedOperationsDialog.css';

/**
 * Everything else the platform recorded near one attempt, in one table.
 *
 * `reason` is the relationship and `detail` is the outcome — kept apart so the
 * table reads down its second column as a list of *why am I seeing this*, which
 * is the question a reader opens this dialog with.
 *
 * See docs/FeatureDesigns/solution-analysis/related-operations-target.md.
 */
const GROUP_LABELS: Record<RelatedOperationRow['group'], string> = {
    'this-attempt': 'This attempt',
    'other-attempts': 'Other attempts at this question',
    'same-sitting': 'Other questions in this sitting',
};

const GROUP_ORDER: RelatedOperationRow['group'][] = ['this-attempt', 'other-attempts', 'same-sitting'];

export function RelatedOperationsDialog({ submissionId, onClose }: {
    submissionId: string;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const { data, isLoading, error } = useQuery({
        queryKey: ['relatedOperations', submissionId],
        queryFn: () => modelApi.getRelatedOperations(submissionId),
    });

    const groups = useMemo(() => GROUP_ORDER
        .map(g => ({ group: g, rows: (data ?? []).filter(r => r.group === g) }))
        .filter(g => g.rows.length > 0), [data]);

    const open = (row: RelatedOperationRow) => {
        if (!row.href) return;
        onClose();
        navigate(row.href);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content related-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Related operations</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {isLoading && <div className="related-empty">Looking…</div>}
                    {error && <div className="related-empty">Couldn't load related operations: {(error as Error).message}</div>}
                    {!isLoading && !error && groups.length === 0 && (
                        <div className="related-empty">Nothing else is recorded against this attempt.</div>
                    )}
                    {groups.map(({ group, rows }) => (
                        <section key={group} className="related-group">
                            <h3>{GROUP_LABELS[group]}</h3>
                            <table className="related-table">
                                <thead>
                                    <tr>
                                        <th>Operation</th>
                                        <th>Time</th>
                                        <th>Reason</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr
                                            key={row.key}
                                            className={[
                                                row.isCurrent ? 'is-current' : '',
                                                row.href ? 'is-link' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => open(row)}
                                        >
                                            <td className="related-op">
                                                {row.operation}
                                                {row.isCurrent && <span className="related-here">you are here</span>}
                                                {row.href && !row.isCurrent && (
                                                    <a
                                                        className="related-open"
                                                        href={row.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open in new tab"
                                                        aria-label={`Open ${row.operation.toLowerCase()} in new tab`}
                                                        // A plain link: the row's own click navigates in place.
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                            <path d="M6.5 3.5H3.5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9.5" />
                                                            <path d="M9.5 2.5h4v4" />
                                                            <path d="M13.5 2.5 7.5 8.5" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </td>
                                            <td className="related-time">
                                                {row.at ? format(new Date(row.at), 'MMM d, HH:mm:ss') : '—'}
                                            </td>
                                            <td>{row.reason}</td>
                                            <td className="related-detail">{row.detail || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
